import { PageHeader } from '@/components/common/PageHeader';
import {
  IUser
} from "@/types";
import { Button, Modal } from 'antd';
import { exportToCSV, generateCSVFilename } from '@/utils/csv';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import React, { useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { UserFilters } from '../components/UserFilters';
import { UserFormDrawer } from '../components/UserFormDrawer';
import { UserStatsCards } from '../components/UserStatsCards';
import { UserTableView } from '../components/UserTableView';
import {
  EditPersonalDetailsModal,
  EditRegionTeamModal,
  EditRoleModal,
} from '../components/modals';
import { useUpdateUser, useUserList, useUserStats } from '../hooks';
import {
  combineFilterQueries,
  generateRegionFilterQuery,
  generateRoleFilterQuery,
  generateStatusFilterQuery,
  generateTeamFilterQuery,
  generateUserSearchQuery,
} from '../utils';
import { useGetCurrentUser } from '@/hooks/app';
import { PermissionChecker } from '@/utils/permission';
import { REGION_ALL, DEFAULT_STARTING_PAGE } from '@/utils/constant/appConstant';

const DEFAULT_USER_FILTER = "contains(domainname,'vhwg.co.uk')";

type ViewMode = 'table' | 'cards' | 'permissions-matrix';

export const UserManagementPageAntd: React.FC = () => {
  // Get current user permissions
  const { data: currentUserData } = useGetCurrentUser();
  const permissions = currentUserData?.permission;

  // Initialize PermissionChecker
  const permissionChecker = new PermissionChecker(permissions);

  // Permission checks
  const canCreateUsers = permissionChecker.canCreateUsers();
  const canEditUsers = permissionChecker.canEditUsers();
  const canDeactivateUsers = permissionChecker.canDeactivateUsers();
  const canManageRoles = permissionChecker.canManageRoles();
  const canChangeRegion = permissionChecker.canChangeRegion();

  // Region filter logic
  const userRegion = currentUserData?.currentUser?.dogma_region;
  const isRegionAll = Boolean(userRegion && userRegion === REGION_ALL);
  const region = !isRegionAll && userRegion ? userRegion : undefined;

  // Query params state for API filtering
  const [queryParams, setQueryParams] = useState({
    current: DEFAULT_STARTING_PAGE,
    pageSize: 20,
    filter: `&$filter=${DEFAULT_USER_FILTER}`,
  });

  // Fetch users from API with filters
  const { data: usersData, isLoading: isLoadingUsers, isRefetching, refetch: refetchUsers } = useUserList({
    queryParams,
    enabled: true,
  });

  // Fetch user statistics
  const { data: statsData, isLoading: isLoadingStats } = useUserStats(region);

  const updateUserMutation = useUpdateUser();
  const users = usersData?.value || [];

  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [sortField, setSortField] = useState<string>('lastname');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // User form state
  const [showUserForm, setShowUserForm] = useState(false);

  // Filter states for UI
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRegion, setFilterRegion] = useState<number | undefined>(undefined);
  const [filterTeam, setFilterTeam] = useState<number | undefined>(undefined);
  const [filterRole, setFilterRole] = useState<string | undefined>(undefined);
  const [filterStatus, setFilterStatus] = useState<number | undefined>(undefined);

  // Edit modals state
  const [showPersonalDetailsModal, setShowPersonalDetailsModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showRegionTeamModal, setShowRegionTeamModal] = useState(false);
  const [editingUser, setEditingUser] = useState<IUser | null>(null);

  // Set default region filter for users without "all regions" access
  useEffect(() => {
    if (userRegion && !isRegionAll) {
      setFilterRegion(userRegion);
      const defaultRegionQuery = generateRegionFilterQuery(userRegion);
      const combined = combineFilterQueries([DEFAULT_USER_FILTER, defaultRegionQuery].filter(Boolean));
      setQueryParams((prev) => ({
        ...prev,
        filter: combined ? "&$filter=" + combined : "&$filter=" + DEFAULT_USER_FILTER,
        current: DEFAULT_STARTING_PAGE,
      }));
    } else if (isRegionAll) {
      setQueryParams((prev) => ({
        ...prev,
        filter: "&$filter=" + DEFAULT_USER_FILTER,
        current: DEFAULT_STARTING_PAGE,
      }));
    }
  }, [userRegion, isRegionAll]);

  // Generate OData filter query
  const updateFilters = (search: string, region: number | undefined, team: number | undefined, role: string | undefined, status: number | undefined) => {
    const queries = [
      DEFAULT_USER_FILTER,
      generateUserSearchQuery(search),
      generateRegionFilterQuery(region || 0),
      generateTeamFilterQuery(team || 0),
      generateRoleFilterQuery(role || ''),
      generateStatusFilterQuery(status || 0),
    ];
    const combinedFilter = combineFilterQueries(queries);

    setQueryParams((prev) => ({
      ...prev,
      filter: combinedFilter ? "&$filter=" + combinedFilter : "",
      current: DEFAULT_STARTING_PAGE,
    }));
  };

  const handleApplyAllFilters = ({ region, team, role, status }: { region: number; team: number; role: string; status: number }) => {
    setFilterRegion(region);
    setFilterTeam(team);
    setFilterRole(role);
    setFilterStatus(status);
    updateFilters(searchQuery, region, team, role, status);
  };

  // Filter change handlers
  const handleSearchTextChange = (value: string) => {
    setSearchQuery(value);
    updateFilters(value, filterRegion, filterTeam, filterRole, filterStatus);
  };

  const handleRegionFilterChange = (value: number) => {
    setFilterRegion(value);
    updateFilters(searchQuery, value, filterTeam, filterRole, filterStatus);
  };

  const handleTeamFilterChange = (value: number) => {
    setFilterTeam(value);
    updateFilters(searchQuery, filterRegion, value, filterRole, filterStatus);
  };

  const handleRoleFilterChange = (value: string) => {
    setFilterRole(value);
    updateFilters(searchQuery, filterRegion, filterTeam, value, filterStatus);
  };

  const handleStatusFilterChange = (value: number) => {
    setFilterStatus(value);
    updateFilters(searchQuery, filterRegion, filterTeam, filterRole, value);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    if (isRegionAll) {
      setFilterRegion(undefined);
    }
    setFilterTeam(undefined);
    setFilterRole(undefined);
    setFilterStatus(undefined);
    if (isRegionAll) {
      updateFilters('', undefined, undefined, undefined, undefined);
    } else {
      updateFilters("", filterRegion, undefined, undefined, undefined);
    }
  };

  // Stats from API
  const stats = {
    total: statsData?.totalUsers || 0,
    active: statsData?.activeUsers || 0,
    inactive: (statsData?.totalUsers || 0) - (statsData?.activeUsers || 0),
    deactivated: statsData?.deactivatedUsers || 0,
    admins: statsData?.administrators || 0,
    creditControl: statsData?.creditControl || 0,
    sales: statsData?.salesTeam || 0,
  };

  const handleTableChange = (current: number, pageSize: number) => {
    setQueryParams((prev) => ({ ...prev, current, pageSize }));
  };

  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle add user
  const handleAddUser = () => {
    setShowUserForm(true);
  };

  // Handle edit personal details
  const handleEditPersonalDetails = (user: IUser) => {
    setEditingUser(user);
    setShowPersonalDetailsModal(true);
  };

  // Handle edit role
  const handleEditRole = (user: IUser) => {
    setEditingUser(user);
    setShowRoleModal(true);
  };

  // Handle edit region/team
  const handleEditRegionTeam = (user: IUser) => {
    setEditingUser(user);
    setShowRegionTeamModal(true);
  };

  // Handle modal success
  const handleModalSuccess = () => {
    refetchUsers();
  };

  // Handle change user status (Active=1, Deactivated=2)
  const handleChangeStatus = (user: IUser) => {
    const isActive = user.dogma_userstatus === 1;  // null/undefined → not active → Activate
    const newStatus = isActive ? 2 : 1;
    const actionLabel = isActive ? 'Deactivate' : 'Activate';

    Modal.confirm({
      title: `${actionLabel} User`,
      content: `Are you sure you want to ${actionLabel.toLowerCase()} ${user.firstname} ${user.lastname}?`,
      okText: actionLabel,
      okType: isActive ? 'danger' : 'primary',
      onOk: async () => {
        try {
          await updateUserMutation.mutateAsync({
            id: user.systemuserid,
            payload: { dogma_userstatus: newStatus },
          });
          refetchUsers();
        } catch (error) {
        }
      },
    });
  };

  const handleExportCSV = () => {
    const csvData = users.map((u) => {
      const defaultRole = u.dogma_AppRole_SystemUser_SystemUser?.find((r: any) => r.dogma_defaultsecurityrole)
        || u.dogma_AppRole_SystemUser_SystemUser?.[0];
      return {
        'First Name': u.firstname || '-',
        'Last Name': u.lastname || '-',
        'Email': u.internalemailaddress || u.domainname || '-',
        'Region': u['dogma_region@OData.Community.Display.V1.FormattedValue'] || '-',
        'Team': u['dogma_team@OData.Community.Display.V1.FormattedValue'] || '-',
        'Role': defaultRole?.dogma_name || '-',
        'Status': u.isdisabled ? 'Inactive' : 'Active',
      };
    });
    const columns = [
      { key: 'First Name' as const, header: 'First Name' },
      { key: 'Last Name' as const, header: 'Last Name' },
      { key: 'Email' as const, header: 'Email' },
      { key: 'Region' as const, header: 'Region' },
      { key: 'Team' as const, header: 'Team' },
      { key: 'Role' as const, header: 'Role' },
      { key: 'Status' as const, header: 'Status' },
    ];
    exportToCSV(csvData, columns, generateCSVFilename('users'));
  };

  // Handle user form submit
  const handleUserFormSubmit = (_userData: any) => {
    // Refetch users after successful creation
    refetchUsers();
    setShowUserForm(false);
  };



  // Main view
  return (
    <>
      {/* Page Header */}
      <div className="px-4 pt-4">
        <PageHeader
          title="User Management"
          subtitle="Manage user accounts, roles, and permissions across the Victorian Sliders system"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'User Management' }
          ]}
          actions={
            <>
              <Button
                icon={<IoCloudDownloadOutline />}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
              {canCreateUsers && (
                <Button
                  type="primary"
                  size="large"
                  icon={<FaPlus />}
                  onClick={handleAddUser}
                >
                  Add User
                </Button>
              )}
            </>
          }
        />
      </div>

      {/* Stats Cards */}
      <div className="p-4">
        <UserStatsCards stats={stats} loading={isLoadingStats} />
      </div>

      {/* Filters */}
      <div className="px-4 pb-4">
        <UserFilters
          searchQuery={searchQuery}
          filterRegion={filterRegion || 0}
          filterStatus={filterStatus || 0}
          filterTeam={filterTeam || 0}
          filterRole={filterRole || ''}
          viewMode={viewMode}
          onSearchChange={handleSearchTextChange}
          onRegionChange={handleRegionFilterChange}
          onTeamChange={handleTeamFilterChange}
          onRoleChange={handleRoleFilterChange}
          onStatusChange={handleStatusFilterChange}
          onApplyFilters={handleApplyAllFilters}
          onClearFilters={handleClearFilters}
          onViewModeChange={setViewMode}
          isRegionAll={isRegionAll}
          userRegion={userRegion}
        />
      </div>

      {/* Users Table View */}
      <div className="px-4 pb-4">
        <div className="bg-white rounded-lg">
          <UserTableView
            users={users}
            loading={isLoadingUsers || isRefetching}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onEditPersonalDetails={handleEditPersonalDetails}
            onEditRole={handleEditRole}
            onEditRegionTeam={handleEditRegionTeam}
            onDelete={handleChangeStatus}
            onChangeStatus={handleChangeStatus}
            canEditUsers={canEditUsers}
            canDeactivateUsers={canDeactivateUsers}
            canManageRoles={canManageRoles}
            canChangeRegion={canChangeRegion}
          />
        </div>
      </div>

      {/* User Form Drawer */}
      <UserFormDrawer
        open={showUserForm}
        onClose={() => {
          setShowUserForm(false);
        }}
        onSubmit={handleUserFormSubmit}
        isRegionAll={isRegionAll}
        userRegion={userRegion}
      />

      {/* Edit Modals */}
      <EditPersonalDetailsModal
        open={showPersonalDetailsModal}
        user={editingUser}
        onClose={() => {
          setShowPersonalDetailsModal(false);
          setEditingUser(null);
        }}
        onSuccess={handleModalSuccess}
      />

      <EditRoleModal
        open={showRoleModal}
        user={editingUser}
        onClose={() => {
          setShowRoleModal(false);
          setEditingUser(null);
        }}
        onSuccess={handleModalSuccess}
      />

      <EditRegionTeamModal
        open={showRegionTeamModal}
        user={editingUser}
        onClose={() => {
          setShowRegionTeamModal(false);
          setEditingUser(null);
        }}
        onSuccess={handleModalSuccess}
        isRegionAll={isRegionAll}
        userRegion={userRegion}
      />
    </>
  );
};

export default UserManagementPageAntd;
